export default function PrivacyContent() {
    return (
        <div>
                <header>
                    <div className="brand">OTUS CAPITAL MANAGEMENT</div>
                    <h1 className="doc-title">Privacy Notice</h1>
                    <p className="meta">
                        <time dateTime="2025-08-13">13 August 2025</time> · Approved by: Board of Directors
                    </p>
                </header>

                <section id="introduction">
                    <h2>Introduction</h2>
                    <p>
                        This Privacy Notice explains how Otus Capital Management (“OCM”) collects, uses, stores, and protects the personal data of visitors to our website in accordance with the UK General Data Protection Regulation (“UK GDPR”) and the Data Protection Act 2018. It also explains your rights and how to exercise them.
                    </p>
                </section>

                <nav className="toc" aria-label="Table of contents">
                    <strong>Contents</strong>
                    <ol>
                        <li><a href="#purpose">1. Purpose of this notice</a></li>
                        <li><a href="#categories">2. Categories of personal data collected</a></li>
                        <li><a href="#collection">3. How we collect your data</a></li>
                        <li><a href="#lawful-bases">4. Lawful bases for processing</a></li>
                        <li><a href="#purposes">5. Purposes of processing</a></li>
                        <li><a href="#sharing">6. Data sharing</a></li>
                        <li><a href="#transfers">7. International data transfer</a></li>
                        <li><a href="#cookies">8. Cookies and tracking technologies</a></li>
                        <li><a href="#retention">9. Data retention</a></li>
                        <li><a href="#rights">10. Your rights</a></li>
                        <li><a href="#security">11. Data security</a></li>
                        <li><a href="#links">12. Links to other websites</a></li>
                        <li><a href="#contact">13. Contact and complaints</a></li>
                    </ol>
                </nav>

                <section id="purpose">
                    <h2>1. Purpose of this notice</h2>
                    <p>
                        This notice sets out how OCM processes personal data collected through our website, ensuring transparency and compliance with our obligations under UK GDPR.
                    </p>
                </section>

                <section id="categories">
                    <h2>2. Categories of personal data collected</h2>
                    <p>We may collect the following types of personal data when you use our website:</p>
                    <ul>
                        <li>Contact information (name, email address, telephone number) submitted via contact forms.</li>
                        <li>Information you provide when subscribing to newsletters or updates.</li>
                        <li>Technical data such as IP address, browser type, operating system, and browsing activity.</li>
                        <li>Cookie and tracking information (see Section 8).</li>
                    </ul>
                </section>

                <section id="collection">
                    <h2>3. How we collect your data</h2>
                    <p>We collect personal data:</p>
                    <ul>
                        <li>Directly from you when you complete forms on our website.</li>
                        <li>Automatically through cookies and similar technologies.</li>
                        <li>From analytics providers that track website usage patterns.</li>
                    </ul>
                </section>

                <section id="lawful-bases">
                    <h2>4. Lawful bases for processing</h2>
                    <p>OCM will only process personal data when at least one lawful basis under Article 6 UK GDPR applies:</p>
                    <ul>
                        <li>Consent (e.g., for marketing communications or non-essential cookies).</li>
                        <li>Performance of a contract or steps taken at your request prior to entering a contract.</li>
                        <li>Compliance with a legal obligation.</li>
                        <li>Legitimate interests, such as improving website functionality and ensuring security.</li>
                    </ul>
                </section>

                <section id="purposes">
                    <h2>5. Purposes of processing</h2>
                    <p>We use personal data collected through our website for the following purposes:</p>
                    <ul>
                        <li>Responding to your enquiries.</li>
                        <li>Providing requested services or information.</li>
                        <li>Sending marketing communications where consent has been given.</li>
                        <li>Analysing and improving website performance.</li>
                        <li>Ensuring website security and preventing fraud.</li>
                    </ul>
                </section>

                <section id="sharing">
                    <h2>6. Data sharing</h2>
                    <p>We may share personal data with:</p>
                    <ul>
                        <li>IT and website hosting providers.</li>
                        <li>Email and marketing service providers.</li>
                        <li>Analytics and cookie service providers.</li>
                        <li>Regulators or law enforcement, where required by law.</li>
                    </ul>
                    <p>All third-party processors are bound by contractual obligations under Article 28 UK GDPR.</p>
                </section>

                <section id="transfers">
                    <h2>7. International data transfer</h2>
                    <p>If your personal data is transferred outside the UK, OCM will ensure appropriate safeguards are in place, such as:</p>
                    <ul>
                        <li>The UK’s International Data Transfer Agreement (IDTA) or Addendum to the EU SCCs.</li>
                        <li>Adequacy regulations granted by the UK Government.</li>
                        <li>Binding Corporate Rules or other recognised safeguards.</li>
                    </ul>
                </section>

                <section id="cookies">
                    <h2>8. Cookies and tracking technologies</h2>
                    <p>
                        We use cookies to enhance your experience, analyse website usage, and deliver targeted advertising. Essential cookies are required for the website to function. Non-essential cookies are only used where you have provided consent. You can manage your cookie preferences through your browser settings or our cookie consent tool.
                    </p>
                </section>

                <section id="retention">
                    <h2>9. Data retention</h2>
                    <p>
                        Personal data collected via the website is retained only for as long as necessary for the purposes for which it was collected, or as required by law. Retention periods are outlined in OCM’s Data Retention Policy.
                    </p>
                </section>

                <section id="rights">
                    <h2>10. Your rights</h2>
                    <p>Under the UK GDPR, you have the following rights:</p>
                    <ul>
                        <li>To be informed about how your data is used.</li>
                        <li>To request access to your personal data.</li>
                        <li>To request correction of your personal data.</li>
                        <li>To request erasure of your personal data.</li>
                        <li>To request restriction of processing.</li>
                        <li>To object to processing.</li>
                        <li>To request data portability.</li>
                        <li>To withdraw consent where consent is the lawful basis.</li>
                    </ul>
                    <p>Requests should be sent to: <a href="mailto:compliance@otuscapital.com">compliance@otuscapital.com</a></p>
                </section>

                <section id="security">
                    <h2>11. Data security</h2>
                    <p>OCM implements technical and organisational measures to protect personal data collected via our website, including:</p>
                    <ul>
                        <li>Secure hosting infrastructure.</li>
                        <li>Encryption of data in transit.</li>
                        <li>Access controls and authentication.</li>
                        <li>Regular security monitoring.</li>
                    </ul>
                </section>

                <section id="links">
                    <h2>12. Links to other websites</h2>
                    <p>
                        Our website may contain links to external websites. OCM is not responsible for the privacy practices of other sites and encourages you to review their privacy notices.
                    </p>
                </section>

                <section id="contact">
                    <h2>13. Contact and complaints</h2>
                    <p>
                        If you have any questions about this notice or how your personal data is handled, please contact:
                    </p>
                    <address>
                        <strong>Compliance Team</strong><br />
                        Otus Capital Management<br />
                        Email: <a href="mailto:compliance@otuscapital.com">compliance@otuscapital.com</a>
                    </address>
                    <p>
                        You also have the right to lodge a complaint with the Information Commissioner’s Office (ICO):
                        <a href="https://ico.org.uk/" target="_blank" rel="noopener">www.ico.org.uk</a> | Telephone: 0303 123 1113
                    </p>
                </section>
            </div>
    );
}